import { useState } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { Check, X } from 'lucide-react'
import { Navbar } from '../layout/Navbar'
import { Button } from '../ui/Button'
import { useNavigate } from 'react-router-dom'

interface CreatePasswordFormValues {
  newPassword: string
  confirmPassword: string
  email?: string
}

interface PasswordValidation {
  key: string
  test: boolean
  message: string
}

const passwordValidationSchema = Yup.object({
  newPassword: Yup.string()
    .min(8, 'Debe tener al menos 8 caracteres')
    .max(20, 'No puede tener más de 20 caracteres')
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      'Debe contener al menos un carácter especial'
    )
    .matches(/[0-9]/, 'Debe contener al menos un número')
    .matches(/[A-Z]/, 'Debe contener al menos una mayúscula')
    .required('Este campo es obligatorio'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Las contraseñas no coinciden')
    .required('Este campo es obligatorio'),
  email: Yup.string().email('Correo electrónico inválido'),
})

interface CreatePasswordFormProps {
  onSubmit: (values: CreatePasswordFormValues) => Promise<void>
  initialEmail?: string
  showEmailField?: boolean
  showSuccessState?: boolean
  passwordMismatch?: boolean
  onInitiateProcess?: () => void
}

export const CreatePasswordForm = ({
  onSubmit,
  initialEmail = '',
  showEmailField = false,
  showSuccessState = false,
  passwordMismatch = false,
  onInitiateProcess,
}: CreatePasswordFormProps) => {
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const navigate = useNavigate()

  const handleInitiateProcess = () => {
    if (onInitiateProcess) {
      onInitiateProcess()
    }

    const getPersonTypeFromStorage = () => {
      try {
        // Primero verificar si ya existe userPersonType (guardado durante login)
        let personType = localStorage.getItem('userPersonType')
        console.log('🏷️ UserPersonType from localStorage:', personType)
        
        if (personType) {
          return personType
        }

        // Fallback: obtener de user_data si no existe userPersonType
        const userData = localStorage.getItem('user_data')
        console.log('📦 Raw userData from localStorage:', userData)
        if (userData) {
          const user = JSON.parse(userData)
          console.log('👤 Parsed user object:', user)
          console.log('🏷️ PersonType from user (fallback):', user.personType)
          
          // Guardar en userPersonType para futuro uso
          if (user.personType) {
            localStorage.setItem('userPersonType', user.personType)
            console.log('💾 PersonType saved to localStorage (fallback):', user.personType)
          }
          
          return user.personType
        }
        return null
      } catch (error) {
        console.error('Error al obtener personType:', error)
        return null
      }
    }

    const personType = getPersonTypeFromStorage()

    if (!personType) {
      console.warn('⚠️ No personType found to save')
    }

    // Navegar a pending-proposals
    console.log(
      '🎯 Navigating to pending-proposals with personType:',
      personType
    )
    navigate('/onboarding/pending-proposals')
  }

  const validatePassword = (password: string): PasswordValidation[] => {
    return [
      {
        key: 'length',
        test: password.length >= 8 && password.length <= 20,
        message: 'Deben ser al menos 8 caracteres y un máximo de 20',
      },
      {
        key: 'special',
        test: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        message: 'Mínimo un carácter especial (ejemplo: $, %, @, etc)',
      },
      {
        key: 'number',
        test: /[0-9]/.test(password),
        message: 'Debe contener al menos un número (0-9)',
      },
      {
        key: 'uppercase',
        test: /[A-Z]/.test(password),
        message: 'Debe tener al menos una mayúscula (A-Z)',
      },
    ]
  }

  const renderSuccessState = () => (
    <>
      {/* Mobile Success Layout */}
      <div className="min-h-screen flex flex-col md:hidden">
        {/* Mobile Header - Solo gradiente */}
        <div
          className="h-48 flex items-center justify-center relative"
          style={{
            background:
              'linear-gradient(180deg, #1789CF 0%, #02298A 84.79%, #021B79 100%)',
            minHeight: '192px',
          }}
        >
          {/* Logo centrado - más grande */}
          <div className="text-center z-10">
            <img
              src="/assets/olr-logo.png"
              alt="OLR Leasing"
              className="filter brightness-0 invert w-40 h-auto mx-auto"
            />
          </div>
        </div>

        {/* Mobile Success Content - menos padding top */}
        <div className="flex-1 bg-white flex items-start justify-center pt-8 px-6 pb-6">
          <div className="text-center max-w-md mx-4 w-full">
            <div className="mb-6">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="text-white" size={32} />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Contraseña actualizada
            </h2>

            <p className="text-gray-600 mb-2">
              Tu contraseña personalizada se ha creado correctamente.
            </p>
            <p className="text-gray-600 mb-8">
              Ya puedes iniciar con tu solicitud.
            </p>

            <Button
              variant="primary"
              className="w-full"
              onClick={handleInitiateProcess}
            >
              Iniciar proceso
            </Button>
          </div>
        </div>
      </div>

      {/* Desktop Success Layout */}
      <div className="hidden md:flex min-h-screen flex-col">
        <Navbar />

        {/* Main content with bg-flow */}
        <div
          className="flex-1 flex items-center justify-center"
          style={{
            backgroundImage: 'url(/assets/bg-flow.svg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="bg-white p-12 rounded-lg text-center max-w-md mx-4">
            <div className="mb-6">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="text-white" size={32} />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Contraseña actualizada
            </h2>

            <p className="text-gray-600 mb-2">
              Tu contraseña personalizada se ha creado correctamente.
            </p>
            <p className="text-gray-600 mb-8">
              Ya puedes iniciar con tu solicitud.
            </p>

            <Button
              variant="primary"
              className="w-full"
              onClick={handleInitiateProcess}
            >
              Iniciar proceso
            </Button>
          </div>
        </div>
      </div>
    </>
  )

  const renderForm = () => (
    <Formik
      initialValues={{
        newPassword: '',
        confirmPassword: '',
        email: initialEmail,
      }}
      validationSchema={passwordValidationSchema}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, values, isValid, dirty }) => {
        const passwordValidations = validatePassword(values.newPassword)
        const hasPasswordMismatch =
          passwordMismatch ||
          (values.confirmPassword &&
            values.newPassword !== values.confirmPassword)

        // Check if all password validations pass
        const allPasswordValidationsPassed = passwordValidations.every(
          validation => validation.test
        )

        // Check if passwords match
        const passwordsMatch =
          values.newPassword &&
          values.confirmPassword &&
          values.newPassword === values.confirmPassword

        // Form is valid when all validations pass, passwords match, and form is dirty
        const isFormValid =
          allPasswordValidationsPassed && passwordsMatch && dirty && isValid

        return (
          <Form className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nueva contraseña
              </label>
              <div className="relative">
                <Field
                  type={showNewPassword ? 'text' : 'password'}
                  name="newPassword"
                  placeholder="Escribe aquí"
                  className="w-full px-3 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                >
                  {showNewPassword ? (
                    <img
                      src="/assets/eye.svg"
                      alt="OLR Leasing"
                      className={`size-5`}
                    />
                  ) : (
                    <img
                      src="/assets/eye-off.svg"
                      alt="OLR Leasing"
                      className={`size-5`}
                    />
                  )}
                </button>
              </div>

              {/* Password validation indicators */}
              <div className="mt-3 space-y-1">
                {passwordValidations.map(validation => (
                  <div
                    key={validation.key}
                    className="flex items-center text-sm"
                  >
                    {validation.test ? (
                      <Check className="text-green-500 mr-2" size={16} />
                    ) : (
                      <X className="text-red-500 mr-2" size={16} />
                    )}
                    <span>{validation.message}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirma tu nueva contraseña
              </label>
              <div className="relative">
                <Field
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  placeholder="Escribe aquí"
                  className={`w-full px-3 py-3 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    hasPasswordMismatch ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                >
                  {showConfirmPassword ? (
                    <img
                      src="/assets/eye.svg"
                      alt="OLR Leasing"
                      className={`size-5`}
                    />
                  ) : (
                    <img
                      src="/assets/eye-off.svg"
                      alt="OLR Leasing"
                      className={`size-5`}
                    />
                  )}
                </button>
              </div>
              {hasPasswordMismatch && (
                <div className="text-red-500 text-sm mt-1">
                  Las contraseñas no coinciden.
                </div>
              )}
            </div>

            {showEmailField && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Correo electrónico
                </label>
                <Field
                  type="email"
                  name="email"
                  placeholder="Escribe aquí"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <ErrorMessage
                  name="email"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={isSubmitting || !isFormValid}
              variant="primary"
              className="w-full"
              isLoading={isSubmitting}
            >
              Guardar contraseña
            </Button>
          </Form>
        )
      }}
    </Formik>
  )

  if (showSuccessState) {
    return renderSuccessState()
  }

  return (
    <>
      {/* Mobile Layout */}
      <div className="min-h-screen flex flex-col md:hidden">
        <Navbar />

        {/* Mobile Form section - menos padding top */}
        <div className="flex-1 bg-white flex items-start justify-center pt-8 px-6 pb-6">
          <div className="w-full max-w-md">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 text-left">
              Crea tu contraseña
            </h2>

            <p className="text-gray-600 text-left mb-8">
              Crea una contraseña nueva, segura y personalizada cumpliendo las
              características solicitadas.
            </p>

            {renderForm()}
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:flex min-h-screen flex-col">
        <Navbar />

        {/* Main content with bg-flow */}
        <div
          className="flex-1 flex items-center justify-center"
          style={{
            backgroundImage: 'url(/assets/bg-flow.svg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="bg-white p-8 rounded-lg max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
              Crea tu contraseña
            </h2>

            <p className="text-gray-600 text-center mb-8">
              Crea una contraseña nueva, segura y personalizada cumpliendo las
              características solicitadas.
            </p>

            {renderForm()}
          </div>
        </div>
      </div>
    </>
  )
}
